// IndexedDB utility functions for storing Magic 8 Ball question history

const DB_NAME = 'magic8ball';
const STORE_NAME = 'questions';
const DB_VERSION = 1;

// Initialize and return database
async function getDB() {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => {
      reject(new Error('Failed to open IndexedDB'));
    };

    request.onsuccess = () => {
      resolve(request.result);
    };

    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      
      // Create object store if it doesn't exist
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const objectStore = db.createObjectStore(STORE_NAME, { 
          keyPath: 'id',
          autoIncrement: false 
        });
        
        // Create index for timestamp to enable sorting
        objectStore.createIndex('timestamp', 'timestamp', { unique: false });
      }
    };
  });
}

// Save questions to IndexedDB
export async function saveQuestions(questions) {
  try {
    const db = await getDB();
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);

    // Clear existing data
    await new Promise((resolve, reject) => {
      const clearRequest = store.clear();
      clearRequest.onsuccess = () => resolve();
      clearRequest.onerror = () => reject(clearRequest.error);
    });

    // Add all questions
    const promises = questions.map(question => {
      // Convert Date object to ISO string for storage
      const questionToStore = {
        ...question,
        timestamp: question.timestamp instanceof Date 
          ? question.timestamp.toISOString() 
          : question.timestamp
      };
      
      return new Promise((resolve, reject) => {
        const request = store.add(questionToStore);
        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
      });
    });

    await Promise.all(promises);
  } catch (error) {
    console.error('Error saving questions to IndexedDB:', error);
  }
}

// Load questions from IndexedDB
export async function loadQuestions() {
  try {
    const db = await getDB();
    const transaction = db.transaction([STORE_NAME], 'readonly');
    const store = transaction.objectStore(STORE_NAME);
    const index = store.index('timestamp');

    return new Promise((resolve, reject) => {
      const request = index.openCursor(null, 'prev'); // Sort by timestamp descending
      const questions = [];

      request.onsuccess = (event) => {
        const cursor = event.target.result;
        if (cursor) {
          // Convert ISO string back to Date object
          const question = {
            ...cursor.value,
            timestamp: new Date(cursor.value.timestamp)
          };
          questions.push(question);
          cursor.continue();
        } else {
          // Sort by id descending (newest first) and limit to 10
          const sortedQuestions = questions
            .sort((a, b) => b.id - a.id)
            .slice(0, 10);
          resolve(sortedQuestions);
        }
      };

      request.onerror = () => {
        reject(request.error);
      };
    });
  } catch (error) {
    console.error('Error loading questions from IndexedDB:', error);
    return [];
  }
}

// Clear all questions from IndexedDB
export async function clearQuestions() {
  try {
    const db = await getDB();
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);

    return new Promise((resolve, reject) => {
      const request = store.clear();
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error('Error clearing questions from IndexedDB:', error);
  }
}

